import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { faPlay, faPause, faDownload } from '@fortawesome/pro-regular-svg-icons';
import Button from '../../../core/components/Button';
import classnames from '../../../core/utils/classnames';
import { withIcon } from '../../../core/components/Icon.tsx';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

const PlayIcon = withIcon(faPlay);
const PauseIcon = withIcon(faPause);
const DownloadIcon = withIcon(faDownload);

interface TranscriptEntry {
    role: string;
    message: string;
    time: string;
}

const formatTime = (timeString: string): string => {
    if (!timeString) return '00:00';

    const totalSeconds = parseFloat(timeString);
    if (isNaN(totalSeconds)) {
        return '00:00';
    }

    const minutes = Math.floor(totalSeconds / 60);
    const seconds = Math.floor(totalSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const getSpeakerNameTranslationKey = (role: string): string => {
    switch (role?.toLowerCase()) {
        case 'agent':
            return 'voiceConversation.speakers.agent';
        case 'user':
            return 'voiceConversation.speakers.client';
        default:
            return role || 'voiceConversation.speakers.unknown';
    }
};

const stripBreakTags = (text: string): string => {
    if (!text) return text;
    // Remove SSML break tags with any time value
    return text.replace(/<break\s+time=['"][^'"]*['"]\s*\/?>/gi, '').trim();
};

const SPEED_OPTIONS = [0.5, 1, 1.5, 2];

interface AudioPlayerProps {
    audioRef: React.RefObject<HTMLAudioElement>;
    audioUrl: string;
    isPlaying: boolean;
    currentTime: number;
    duration: number;
    isLoading: boolean;
    playbackSpeed: number;
    onPlayPause: () => void;
    onSeek: (value: number[]) => void;
    onSpeedChange: () => void;
    onLoadedMetadata: () => void;
    onTimeUpdate: () => void;
    onEnded: () => void;
    onError: React.ReactEventHandler<HTMLAudioElement>;
}

const AudioPlayer = ({
    audioRef,
    audioUrl,
    isPlaying,
    currentTime,
    duration,
    isLoading,
    playbackSpeed,
    onPlayPause,
    onSeek,
    onSpeedChange,
    onLoadedMetadata,
    onTimeUpdate,
    onEnded,
    onError,
}: AudioPlayerProps) => {
    const { t } = useTranslation('assistance');

    return (
        <div className="sticky top-0 z-10 bg-surface-weak border-b border-solid border-line-weak p-6 shadow-sm">
            <div className="flex items-center gap-4 w-full">
                <Button
                    variant="ghost"
                    className="w-10 h-10 p-0 flex items-center justify-center"
                    onClick={onPlayPause}
                    disabled={isLoading}
                >
                    {isPlaying ? <PauseIcon /> : <PlayIcon />}
                </Button>

                <div className="text-sm text-content-strong min-w-[3rem] text-right">
                    {formatTime(currentTime.toString())}
                </div>

                <SliderPrimitive.Root
                    className="relative flex items-center select-none w-full flex-1 h-5"
                    min={0}
                    max={duration || 100}
                    step={0.1}
                    value={[currentTime]}
                    onValueChange={onSeek}
                    disabled={isLoading}
                >
                    <SliderPrimitive.Track className="bg-surface-strong relative flex-1 rounded-full h-1">
                        <SliderPrimitive.Range className="absolute bg-surface-brand-strong-hover rounded-full h-full" />
                    </SliderPrimitive.Track>
                    <SliderPrimitive.Thumb className="block w-4 h-4 bg-surface-weak rounded-full shadow-sm hover:shadow cursor-grab border border-solid border-line-weak outline-line-brand-weak" />
                </SliderPrimitive.Root>

                <div className="text-sm text-content-strong min-w-[3rem]">{formatTime(duration.toString())}</div>

                <Button
                    variant="ghost"
                    className="w-10 h-10 p-0 flex items-center justify-center text-sm"
                    onClick={onSpeedChange}
                    disabled={isLoading}
                >
                    {playbackSpeed}x
                </Button>

                <a
                    href={audioUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classnames(
                        'inline-flex items-center justify-center w-10 h-10 p-0 rounded',
                        'bg-surface-weak text-content-strong border border-solid border-line-transparent',
                        'transition-all',
                        'hover:bg-surface-strong hover:text-content-strong',
                        isLoading && 'opacity-50 pointer-events-none'
                    )}
                    aria-label={t('voiceConversation.audioPlayer.downloadRecording')}
                >
                    <DownloadIcon />
                </a>
            </div>

            <audio
                ref={audioRef}
                src={audioUrl}
                onLoadedMetadata={onLoadedMetadata}
                onTimeUpdate={onTimeUpdate}
                onEnded={onEnded}
                onError={onError}
            />
        </div>
    );
};

interface TranscriptMessageProps {
    entry: TranscriptEntry;
    index: number;
    isActive: boolean;
    callerName?: string;
    onClick: (timeString: string) => void;
    ref?: React.Ref<HTMLDivElement>;
}

const TranscriptMessage = ({ entry, index, isActive, callerName, onClick, ref }: TranscriptMessageProps) => {
    const isAgent = entry.role?.toLowerCase() === 'agent';
    const { t } = useTranslation('assistance');

    const speakerLabel = !isAgent && callerName ? callerName : t(getSpeakerNameTranslationKey(entry.role));

    return (
        <div
            ref={ref}
            className={classnames(
                'flex items-start gap-2 transition-all cursor-pointer text-content-strong',
                isAgent
                    ? 'flex-row-reverse ml-auto bg-surface-strong p-2 rounded-md max-w-[85%]'
                    : 'flex-row mr-auto bg-surface-weak border border-solid border-line-weak p-4 rounded-lg shadow-sm max-w-[75%]',
                'hover:opacity-90',
                isActive && 'ring-2 ring-line-brand-default'
            )}
            onClick={() => onClick(entry.time)}
        >
            <div className={classnames('text-sm min-w-[3rem]', 'text-content-weak')}>{formatTime(entry.time)}</div>
            <div className="flex flex-col items-start p-0 gap-1 flex-grow">
                <div className="font-semibold text-sm">{speakerLabel}</div>
                <div className="text-sm whitespace-pre-wrap">{stripBreakTags(entry.message)}</div>
            </div>
        </div>
    );
};

const VoiceConversationView = ({ record, loading }) => {
    const { t } = useTranslation('assistance');

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);

    // Refs to store values across renders
    const audioRef = useRef<HTMLAudioElement>(null);
    const transcriptRefs = useRef<Map<number, HTMLDivElement>>(new Map());
    const isSeekingRef = useRef(false);
    const lastAutoScrollRef = useRef(0);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-sm text-content-strong">{t('voiceConversation.states.loading')}</div>
            </div>
        );
    }

    const voiceData = record?.voiceAgentRecord;
    const audioUrl = voiceData?.audioRecordingUrl;
    const callerName = voiceData?.callerName;
    const callerPhoneNumber = voiceData?.callerPhoneNumber;
    const transcriptEntries: TranscriptEntry[] = voiceData?.transcript?.entries || [];

    if (!audioUrl) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-sm text-content-strong">{t('voiceConversation.states.noAudioAvailable')}</div>
            </div>
        );
    }

    // Helper function to find active transcript index
    const getActiveTranscriptIndex = useCallback((currentTime: number, entries: TranscriptEntry[]): number => {
        if (!entries || entries.length === 0) return -1;

        for (let i = entries.length - 1; i >= 0; i--) {
            const entryTime = parseFloat(entries[i].time);
            if (!isNaN(entryTime) && entryTime <= currentTime) {
                return i;
            }
        }

        return -1;
    }, []);

    const activeIndex = useMemo(() => {
        return getActiveTranscriptIndex(currentTime, transcriptEntries);
    }, [currentTime, transcriptEntries, getActiveTranscriptIndex]);

    const handleLoadedMetadata = useCallback(() => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
            setIsLoading(false);
        }
    }, []);

    const handleTimeUpdate = useCallback(() => {
        if (audioRef.current && !isSeekingRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    }, []);

    const handleEnded = useCallback(() => {
        setIsPlaying(false);
        setCurrentTime(0);
        if (audioRef.current) {
            audioRef.current.currentTime = 0;
        }
    }, []);

    const handleError = useCallback((e: React.SyntheticEvent<HTMLAudioElement>) => {
        console.error('Audio loading error:', e);
        setIsLoading(false);
    }, []);

    const handlePlayPause = useCallback(() => {
        if (!audioRef.current) return;

        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    }, [isPlaying]);

    const handleSeek = useCallback((value: number[]) => {
        if (!audioRef.current) return;

        const newTime = value[0];
        isSeekingRef.current = true;
        audioRef.current.currentTime = newTime;
        setCurrentTime(newTime);

        setTimeout(() => {
            isSeekingRef.current = false;
        }, 100);
    }, []);

    const handleTranscriptClick = useCallback(
        (timeString: string) => {
            const time = parseFloat(timeString);
            if (isNaN(time) || !audioRef.current) return;

            isSeekingRef.current = true;
            audioRef.current.currentTime = time;
            setCurrentTime(time);

            setTimeout(() => {
                isSeekingRef.current = false;
            }, 100);
        },
        [isPlaying]
    );

    const handleSpeedChange = useCallback(() => {
        const currentIndex = SPEED_OPTIONS.indexOf(playbackSpeed);
        const nextSpeed = SPEED_OPTIONS[(currentIndex + 1) % SPEED_OPTIONS.length];

        if (audioRef.current) {
            audioRef.current.playbackRate = nextSpeed;
            setPlaybackSpeed(nextSpeed);
        }
    }, [playbackSpeed]);

    // Auto-scroll effect
    useEffect(() => {
        if (activeIndex === -1 || isSeekingRef.current) return;

        const element = transcriptRefs.current.get(activeIndex);
        if (!element) return;

        const now = Date.now();
        if (now - lastAutoScrollRef.current < 500) return;

        element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
        });

        lastAutoScrollRef.current = now;
    }, [activeIndex]);

    // Keyboard event listener
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

            if (e.code === 'Space') {
                e.preventDefault();
                handlePlayPause();
            } else if (e.code === 'ArrowLeft') {
                e.preventDefault();
                handleSeek([Math.max(0, currentTime - 5)]);
            } else if (e.code === 'ArrowRight') {
                e.preventDefault();
                handleSeek([Math.min(duration, currentTime + 5)]);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handlePlayPause, handleSeek, currentTime, duration]);

    return (
        <div className="flex flex-col w-full h-full overflow-hidden">
            <AudioPlayer
                audioRef={audioRef}
                audioUrl={audioUrl}
                isPlaying={isPlaying}
                currentTime={currentTime}
                duration={duration}
                isLoading={isLoading}
                playbackSpeed={playbackSpeed}
                onPlayPause={handlePlayPause}
                onSeek={handleSeek}
                onSpeedChange={handleSpeedChange}
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
                onEnded={handleEnded}
                onError={handleError}
            />
            {callerName && (
                <div className="px-6 py-3 border-b border-solid border-line-weak bg-surface-weak">
                    <span className="text-sm text-content-strong font-medium">
                        {t('voiceConversation.callerInfo.name')}: {callerName}
                    </span>
                    {callerPhoneNumber && <span className="text-sm text-content-weak ml-2">· {callerPhoneNumber}</span>}
                </div>
            )}
            <div className="flex-1 p-6 overflow-y-auto">
                {transcriptEntries.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-sm text-content-strong">
                            {t('voiceConversation.states.noTranscriptAvailable')}
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-1">
                        {transcriptEntries.map((entry, index) => (
                            <TranscriptMessage
                                key={index}
                                entry={entry}
                                index={index}
                                isActive={index === activeIndex}
                                callerName={callerName}
                                onClick={handleTranscriptClick}
                                ref={(element) => {
                                    if (element) {
                                        transcriptRefs.current.set(index, element);
                                    } else {
                                        transcriptRefs.current.delete(index);
                                    }
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default VoiceConversationView;
